'use client';

import { useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import DocContent from '@/components/DocContent';
import { useOrderStore } from '@/store/useOrderStore';
import { calculateMultiItemDiscounts, calcScheduleCharges, congestionMultiplierForSchedule } from '@/lib/price-calculator';
import { resolveOrderCarryHelp, CARRY_HELP_DISCOUNT_RATE } from '@/lib/carry-help';
import { Loader2 } from 'lucide-react';
import TossPaymentWidget, { type TossPaymentWidgetRef } from '@/components/payment/TossPaymentWidget';
import { trackEvent } from '@/lib/analytics';
import { cn } from '@/lib/utils';
import { useAdminMode } from '@/hooks/useAdminMode';

type Phase = 'info' | 'payment';

interface Props {
  /** 동시주문 충돌(직접주문)로 일정 재선택이 필요할 때 호출 — 일정 스텝으로 이동 */
  onRequireReschedule?: () => void;
}

export default function Step5Payment({ onRequireReschedule }: Props = {}) {
  const {
    itemInfo,
    items,
    origin,
    destination,
    priceBreakdown,
    distanceKm,
    scheduleDate,
    designatedPickupSlot,
    designatedDeliverySlot,
    flexiblePickupMorning,
    flexiblePickupAfternoon,
    flexibleDeliveryMorning,
    flexibleDeliveryAfternoon,
    flexibleDeliveryAnytime,
    showDeliveryTimePanel,
    pickupDiscountRate,
    deliveryDiscountRate,
    phone,
    originPhone,
    additionalNote,
    termsAgreed,
    appliedCouponCode,
    quoteId,
    lockedFinalPrice,
    quoteExtraItem,
    quoteCarryHelp,
    carryHelpOrigin,
    carryHelpDest,
    couponDiscount,
    slotSelectedAt,
    setSlotConflictNotice,
    setPhone,
    setOriginPhone,
    setAdditionalNote,
    setTermsAgreed,
    setAllTermsAgreed,
    setCarryHelp,
    setCoupon,
  } = useOrderStore();

  // 복수 물품 요약
  const categoryLabel = items.length >= 2
    ? `${items[0].displayName} 외 ${items.length - 1}건`
    : items.length === 1
      ? items[0].displayName
      : itemInfo.category;

  // 다물품 할인 적용
  const discountMap = useMemo(() => calculateMultiItemDiscounts(items), [items]);

  const totalItemBasePrice = items.length > 0
    ? items.reduce((sum, i) => {
        const d = discountMap.get(i.uid);
        return sum + (d ? d.discountedPrice : i.basePrice);
      }, 0)
    : itemInfo.basePrice;

  const [phase, setPhase] = useState<Phase>('info');
  const [processing, setProcessing] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  // 서버가 확정·저장한 실제 청구 금액 (견적 고정가/위치보정 등 서버 재계산 반영). 결제는 반드시 이 값으로.
  const [chargeAmount, setChargeAmount] = useState<number | null>(null);
  const [docDialog, setDocDialog] = useState<{ open: boolean; title: string; content: string }>({
    open: false,
    title: '',
    content: '',
  });
  const [docLoading, setDocLoading] = useState(false);
  const [noticeAgreed, setNoticeAgreed] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponSuccess, setCouponSuccess] = useState<string | null>(null);

  const paymentWidgetRef = useRef<TossPaymentWidgetRef>(null);

  const openDoc = async (type: 'terms' | 'privacy' | 'marketing', title: string) => {
    setDocLoading(true);
    setDocDialog({ open: true, title, content: '' });
    try {
      const res = await fetch(`/api/docs?type=${type}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setDocDialog({ open: true, title, content: data.content });
    } catch {
      setDocDialog({ open: true, title, content: '문서를 불러올 수 없습니다.' });
    } finally {
      setDocLoading(false);
    }
  };

  // 전화번호 포맷팅
  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
  };

  const handleOriginPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setOriginPhone(formatted);
  };

  // 운반도움 분류 / 할인 계산
  // 견적 링크(quoteCarryHelp != null)면 관리자 지정 모드 + 전체 기본가 기준, 아니면 items 기준
  const effectiveItems = items.length > 0
    ? items
    : (itemInfo.itemId ? [{ carryHelp: itemInfo.carryHelp, basePrice: itemInfo.basePrice }] : []);
  const orderCarry = resolveOrderCarryHelp(effectiveItems);
  const carryHelpMode = quoteCarryHelp != null ? quoteCarryHelp : orderCarry.mode;
  const carryHelpDiscountBase = quoteCarryHelp != null
    ? effectiveItems.reduce((s, i) => s + (i.basePrice || 0), 0)
    : orderCarry.discountBase;
  // PARKING side 는 운반도움 대상 아님 (체크박스 미표시·필수 강제 X·할인 X)
  const carryHelpOriginApplicable = carryHelpMode !== 'none' && origin?.locationType !== 'PARKING';
  const carryHelpDestApplicable = carryHelpMode !== 'none' && destination?.locationType !== 'PARKING';
  const carryHelpPerSide = carryHelpMode === 'discount'
    ? Math.round(carryHelpDiscountBase * CARRY_HELP_DISCOUNT_RATE)
    : 0;
  const carryHelpDiscount = carryHelpMode === 'discount'
    ? (carryHelpOriginApplicable && carryHelpOrigin ? carryHelpPerSide : 0) +
      (carryHelpDestApplicable && carryHelpDest ? carryHelpPerSide : 0)
    : 0;
  // 필수 모드: 적용 가능한 side 는 모두 체크되어야 결제 가능
  const carryHelpRequiredSatisfied = carryHelpMode !== 'required' ||
    ((!carryHelpOriginApplicable || carryHelpOrigin) && (!carryHelpDestApplicable || carryHelpDest));

  const isPhoneValid = /^01[016789]-\d{3,4}-\d{4}$/.test(phone);
  const isOriginPhoneValid = /^01[016789]-\d{3,4}-\d{4}$/.test(originPhone);
  const isTermsValid = termsAgreed.terms && termsAgreed.privacy;
  const canPay = isPhoneValid && isOriginPhoneValid && isTermsValid && priceBreakdown && noticeAgreed && carryHelpRequiredSatisfied;
  const { isAdmin } = useAdminMode();
  const [attempted, setAttempted] = useState(false);
  const phoneError = attempted && !isPhoneValid;
  const originPhoneError = attempted && !isOriginPhoneValid;
  const termsError = attempted && !isTermsValid;
  const noticeError = attempted && !noticeAgreed;
  const carryHelpError = attempted && !carryHelpRequiredSatisfied;

  // 유연시간 할인/할증 + 도착시간 할증을 파생값으로 계산 (저가 물품 규칙 포함)
  const flexCount =
    (flexiblePickupMorning ? 1 : 0) +
    (flexiblePickupAfternoon ? 1 : 0);
  const hasDesignatedDelivery = !!designatedDeliverySlot;
  // 교통체증 할증: 수거시간대 기준 거리할증 배수 → 추가분을 기본 운송비에 합산(서버와 동일하게 일정할인 이전)
  const congestionMult = congestionMultiplierForSchedule(scheduleDate, designatedPickupSlot);
  const congestionExtra = Math.round((priceBreakdown?.distanceSurcharge ?? 0) * (congestionMult - 1));
  const baseTransport = (priceBreakdown?.totalPrice ?? 0) + congestionExtra;
  const charges = priceBreakdown
    ? calcScheduleCharges({
        baseTotal: baseTransport,
        itemBasePrice: priceBreakdown.itemBasePrice,
        flexCount,
        hasDesignatedPickup: !!designatedPickupSlot,
        hasDesignatedDelivery,
        pickupDiscountRate,
        deliveryDiscountRate,
      })
    : null;
  // 견적 추가 항목 (양수=가산, 음수=할인). 일정 할증/할인까지 반영된 finalPrice 에 평면 가산
  const extraItemAmount = quoteExtraItem?.amount ?? 0;
  // 확정 견적 고정가가 있으면 그 값을 기준으로(= 서버 청구액과 동일). 운반도움 할인만 위에 차감.
  // extra·일정·위치는 이미 고정가에 포함돼 있으므로 더하지 않는다.
  const finalPrice = lockedFinalPrice != null
    ? Math.max(0, lockedFinalPrice - carryHelpDiscount)
    : (charges?.finalPrice ?? 0) + (charges ? extraItemAmount : 0) - (charges ? carryHelpDiscount : 0);
  const flexDiscount = charges?.flexDiscount ?? 0;
  const slotDiscount = charges?.slotDiscount ?? 0;
  const deliverySurcharge = charges?.deliverySurcharge ?? 0;
  const designatedPickupSurcharge = charges?.designatedPickupSurcharge ?? 0;
  const singleFlexSurcharge = charges?.singleFlexSurcharge ?? 0;

  // 쿠폰 ���용 후 최종 금액
  const finalPriceAfterCoupon = Math.max(0, Math.round((finalPrice - couponDiscount) / 100) * 100);

  // 쿠폰 적용
  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    setCouponError(null);
    setCouponSuccess(null);
    try {
      const res = await fetch('/api/coupons/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: couponInput.trim(), totalPrice: finalPrice }),
      });
      const data = await res.json();
      if (data.valid) {
        setCoupon(data.coupon.code, data.coupon.discountAmount);
        setCouponSuccess(
          data.coupon.discountType === 'PERCENTAGE'
            ? `${data.coupon.discountValue}% 할인 적용 (-${data.coupon.discountAmount.toLocaleString()}원)`
            : `${data.coupon.discountAmount.toLocaleString()}원 할인 적용`
        );
        setCouponError(null);
      } else {
        setCouponError(data.message || '유효하지 않은 쿠폰입니다.');
        setCoupon(null, 0);
        setCouponSuccess(null);
      }
    } catch {
      setCouponError('쿠폰 확인 중 오류가 발생했습��다.');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCoupon(null, 0);
    setCouponInput('');
    setCouponSuccess(null);
    setCouponError(null);
  };

  // Phase 1: 주문 생성 (PENDING) — 이미 생성된 주문이 있으면 재사용
  const handleCreateOrder = async () => {
    if (!canPay) return;

    // 이전 시도에서 이미 PENDING 주문이 생성되었으면 바로 결제 단계로
    if (orderId) {
      setPhase('payment');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const orderRes = await fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          itemId: items.length > 0 ? items[0].itemId : itemInfo.itemId,
          category: categoryLabel,
          itemOptions: items.length > 0 ? items[0].options : itemInfo.options,
          isCustomItem: itemInfo.isCustom,
          customItemData: itemInfo.customData,
          itemBasePrice: totalItemBasePrice,
          items: items.length > 0 ? items : undefined,
          origin,
          destination,
          distanceKm,
          priceBreakdown,
          totalPrice: finalPrice,
          scheduleDate,
          designatedPickupSlot,
          designatedDeliverySlot,
          flexiblePickupMorning,
          flexiblePickupAfternoon,
          flexibleDeliveryMorning,
          flexibleDeliveryAfternoon,
          flexibleDeliveryAnytime,
          showDeliveryTimePanel,
          pickupDiscountRate,
          deliveryDiscountRate,
          phone,
          originPhone: originPhone || undefined,
          additionalNote: additionalNote || undefined,
          carryHelpOrigin: carryHelpOriginApplicable && carryHelpOrigin,
          carryHelpDest: carryHelpDestApplicable && carryHelpDest,
          // 단일 물품(직접) 케이스의 분류 — 서버가 items[] 없이 분류/할인 기준가를 산출하기 위함
          itemCarryHelp: itemInfo.carryHelp,
          couponCode: appliedCouponCode || undefined,
          quoteId: quoteId || undefined,
          slotSelectedAt: slotSelectedAt ?? undefined,
        }),
      });

      if (!orderRes.ok) {
        const errData = await orderRes.json().catch(() => null);
        // 동시주문 슬롯 충돌: 직접주문은 일정 재선택으로 이동, 견적주문은 안내만(결제 차단)
        if (errData?.error === 'SLOT_CONFLICT') {
          if (quoteId) {
            setError(errData.message || '해당 시간이 마감되었습니다. 새 견적을 요청해 주세요.');
          } else {
            setSlotConflictNotice(errData.message || '인접 시간대에 다른 예약이 등록되었습니다. 배송 일정을 다시 선택해 주세요.');
            onRequireReschedule?.();
          }
          return;
        }
        throw new Error(errData?.message || '주문 생성 실패');
      }

      const data = await orderRes.json();
      setOrderId(data.orderId);
      // 서버가 저장한 totalPrice 를 실제 청구 금액으로 사용 (payments/confirm 이 이 값과 일치해야 통과).
      const serverTotal = typeof data.order?.totalPrice === 'number' ? data.order.totalPrice : finalPriceAfterCoupon;
      setChargeAmount(serverTotal);
      trackEvent('begin_checkout', { value: serverTotal, currency: 'KRW' });
      setPhase('payment');
    } catch (err) {
      setError(err instanceof Error ? err.message : '예약 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessing(false);
    }
  };

  // Phase 2: 결제 요청
  const handleRequestPayment = async () => {
    if (!paymentWidgetRef.current) return;

    setPaymentProcessing(true);
    setError(null);

    try {
      await paymentWidgetRef.current.requestPayment();
    } catch (err: unknown) {
      const error = err as { code?: string; message?: string };
      if (error?.code === 'USER_CANCEL') {
        // 사용자 취소는 정상 플로우
      } else {
        setError(error?.message || '결제 요청에 실패했습니다.');
      }
    } finally {
      setPaymentProcessing(false);
    }
  };

  const formatPrice = (price: number) => price.toLocaleString() + '원';

  return (
    <div className="space-y-4">
      {/* 최종 운송비 요약 */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
        {items.length >= 2 ? (
          items.map((item) => {
            const disc = discountMap.get(item.uid);
            const hasDiscount = disc && disc.discountRate > 0;
            return (
              <div key={item.uid} className="flex justify-between text-sm">
                <span className="text-gray-600">{item.displayName}</span>
                {hasDiscount ? (
                  <span className="flex items-center gap-1.5">
                    <span className="text-gray-400 line-through text-xs">{item.basePrice.toLocaleString()}원</span>
                    <span className="text-xs text-red-500">-{Math.round(disc.discountRate * 100)}%</span>
                    <span>{disc.discountedPrice.toLocaleString()}원</span>
                  </span>
                ) : (
                  <span>{item.basePrice.toLocaleString()}원</span>
                )}
              </div>
            );
          })
        ) : (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">물품</span>
            <span>{categoryLabel}</span>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">경로</span>
          <span className="truncate max-w-[200px]">
            {origin?.address.split(' ')[1]} → {destination?.address.split(' ')[1]}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">일정</span>
          <span>{scheduleDate}</span>
        </div>
        {designatedPickupSlot && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">수거 지정시간</span>
            <span>{designatedPickupSlot}</span>
          </div>
        )}
        {designatedDeliverySlot && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">배송 지정시간</span>
            <span>{designatedDeliverySlot}</span>
          </div>
        )}

        <Separator className="my-2" />

        <div className="flex justify-between text-sm">
          <span className="text-gray-600">
            기본 운송비
            {congestionExtra > 0 && <span className="text-orange-600"> (교통체증시간대 추가 적용)</span>}
          </span>
          <span>{priceBreakdown && formatPrice(baseTransport)}</span>
        </div>
        {flexDiscount > 0 && (
          <div className="flex justify-between text-sm text-green-600">
            <span>유연시간 할인</span>
            <span>-{formatPrice(flexDiscount)}</span>
          </div>
        )}
        {slotDiscount > 0 && (
          <div className="flex justify-between text-sm text-green-600">
            <span>최적시간 할인</span>
            <span>-{formatPrice(slotDiscount)}</span>
          </div>
        )}
        {designatedPickupSurcharge > 0 && (
          <div className="flex justify-between text-sm text-orange-600">
            <span>수거시간 지정 할증</span>
            <span>+{formatPrice(designatedPickupSurcharge)}</span>
          </div>
        )}
        {singleFlexSurcharge > 0 && (
          <div className="flex justify-between text-sm text-orange-600">
            <span>유연시간 1개 할증</span>
            <span>+{formatPrice(singleFlexSurcharge)}</span>
          </div>
        )}
        {deliverySurcharge > 0 && (
          <div className="flex justify-between text-sm text-orange-600">
            <span>도착시간 지정</span>
            <span>+{formatPrice(deliverySurcharge)}</span>
          </div>
        )}
        {extraItemAmount !== 0 && quoteExtraItem && (
          <div className={`flex justify-between text-sm ${extraItemAmount < 0 ? 'text-green-600' : 'text-orange-600'}`}>
            <span>{quoteExtraItem.name}</span>
            <span>{extraItemAmount > 0 ? '+' : '-'}{formatPrice(Math.abs(extraItemAmount))}</span>
          </div>
        )}
        {carryHelpDiscount > 0 && (
          <div className="flex justify-between text-sm text-green-600">
            <span>운반도움 할인</span>
            <span>-{formatPrice(carryHelpDiscount)}</span>
          </div>
        )}
        {couponDiscount > 0 && (
          <div className="flex justify-between text-sm text-green-600">
            <span>쿠폰 할인 ({appliedCouponCode})</span>
            <span>-{formatPrice(couponDiscount)}</span>
          </div>
        )}

        <Separator className="my-2" />

        <div className="flex justify-between font-bold">
          <span className="text-[#1A3A5C]">최종 결제 금액</span>
          <span className="text-xl text-[#1A3A5C]">{formatPrice(finalPriceAfterCoupon)}</span>
        </div>
      </div>

      {/* 쿠폰 입력 */}
      {phase === 'info' && (
        <div className="space-y-2">
          <Label>쿠폰 코드</Label>
          <div className="flex gap-2">
            <Input
              value={couponInput}
              onChange={(e) => setCouponInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
              placeholder="쿠폰 코드 입력"
              maxLength={10}
              className="font-mono tracking-wider"
              disabled={couponLoading}
            />
            {appliedCouponCode ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleRemoveCoupon}
                className="shrink-0"
              >
                취소
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={handleApplyCoupon}
                disabled={!couponInput.trim() || couponLoading}
                className="shrink-0"
              >
                {couponLoading ? '확인 중...' : '적용'}
              </Button>
            )}
          </div>
          {couponSuccess && (
            <p className="text-sm text-green-600">{couponSuccess}</p>
          )}
          {couponError && (
            <p className="text-sm text-red-500">{couponError}</p>
          )}
        </div>
      )}

      {/* Phase 1: 정보 입력 */}
      {phase === 'info' && (
        <>
          {/* 운반 도움 (필수 / 할인) */}
          {carryHelpMode !== 'none' && (carryHelpOriginApplicable || carryHelpDestApplicable) && (
            <div className={cn(
              'rounded-lg p-4 space-y-3',
              carryHelpError ? 'bg-amber-50 border border-red-500 ring-2 ring-red-200' : 'bg-amber-50 border border-amber-200',
            )}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-amber-900">운반 도움</span>
                {carryHelpMode === 'required'
                  ? <span className="text-xs text-red-500 font-medium">필수</span>
                  : <span className="text-xs text-green-700 font-medium">선택 시 할인</span>}
              </div>
              <p className="text-xs text-amber-700">
                {carryHelpMode === 'required'
                  ? '선택하신 물품은 운반에 도움이 필요합니다. 출발지/도착지 운반 도움 제공에 동의해주세요.'
                  : `운반을 도와주시면 출발지/도착지 각 ${formatPrice(carryHelpPerSide)} 할인됩니다.`}
              </p>
              {carryHelpOriginApplicable && (
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox checked={carryHelpOrigin} onCheckedChange={(c) => setCarryHelp('origin', !!c)} />
                  <span className="text-sm text-gray-700">
                    출발지 운반 도움
                    {carryHelpMode === 'required'
                      ? <span className="text-red-500 ml-1">(필수)</span>
                      : <span className="text-green-600 ml-1">(-{formatPrice(carryHelpPerSide)})</span>}
                  </span>
                </label>
              )}
              {carryHelpDestApplicable && (
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox checked={carryHelpDest} onCheckedChange={(c) => setCarryHelp('dest', !!c)} />
                  <span className="text-sm text-gray-700">
                    도착지 운반 도움
                    {carryHelpMode === 'required'
                      ? <span className="text-red-500 ml-1">(필수)</span>
                      : <span className="text-green-600 ml-1">(-{formatPrice(carryHelpPerSide)})</span>}
                  </span>
                </label>
              )}
              {carryHelpError && (
                <p className="text-sm text-red-500">출발지/도착지 운반 도움 제공에 동의해주세요</p>
              )}
            </div>
          )}

          {/* 연락처 입력 */}
          <div className="space-y-2">
            <Label>도착지 연락처 (배송 안내 문자 수신용) <span className="text-red-500">*</span></Label>
            <Input
              type="tel"
              inputMode="numeric"
              value={phone}
              onChange={handlePhoneChange}
              placeholder="010-0000-0000"
              maxLength={13}
              className={cn(phoneError && 'border-red-500 ring-2 ring-red-200')}
            />
            {phone && !isPhoneValid && (
              <p className="text-sm text-red-500">올바른 휴대폰 번호를 입력해주세요</p>
            )}
            {attempted && !phone && (
              <p className="text-sm text-red-500">연락처를 입력해주세요</p>
            )}
          </div>

          {/* 출발지 연락처 */}
          <div className="space-y-2">
            <Label>출발지 연락처 <span className="text-red-500">*</span></Label>
            <Input
              type="tel"
              inputMode="numeric"
              value={originPhone}
              onChange={handleOriginPhoneChange}
              placeholder="010-0000-0000"
              maxLength={13}
              className={cn(originPhoneError && 'border-red-500 ring-2 ring-red-200')}
            />
            {!isOriginPhoneValid && originPhone && (
              <p className="text-sm text-red-500">올바른 휴대폰 번호를 입력해주세요</p>
            )}
            {attempted && !originPhone && (
              <p className="text-sm text-red-500">출발지 연락처를 입력해주세요</p>
            )}
          </div>

          {/* 약관 동의 */}
          {termsError && (
            <p className="text-sm text-red-500">이용약관과 개인정보 처리방침에 동의해주세요</p>
          )}
          <div className={cn(
            'rounded-lg p-4 space-y-3',
            termsError ? 'bg-gray-50 border border-red-500 ring-2 ring-red-200' : 'bg-gray-50',
          )}>
            {/* 전체 동의 */}
            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={termsAgreed.terms && termsAgreed.privacy && termsAgreed.marketing}
                onCheckedChange={(checked) => setAllTermsAgreed(!!checked)}
              />
              <span className="font-medium text-gray-900">전체 동의</span>
            </label>

            <Separator />

            {/* 개별 약관 */}
            {[
              { key: 'terms' as const, docType: 'terms' as const, label: '이용약관 동의', required: true },
              { key: 'privacy' as const, docType: 'privacy' as const, label: '개인정보 처리방침 동의', required: true },
              { key: 'marketing' as const, docType: 'marketing' as const, label: '마케팅 수신 동의', required: false },
            ].map((term) => (
              <div key={term.key} className="flex items-center justify-between">
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={termsAgreed[term.key]}
                    onCheckedChange={(checked) => setTermsAgreed(term.key, !!checked)}
                  />
                  <span className="text-sm text-gray-700">
                    {term.label}
                    <span className="text-gray-400 ml-1">
                      ({term.required ? <><span className="text-red-500">*</span> 필수</> : '선택'})
                    </span>
                  </span>
                </label>
                <button
                  type="button"
                  className="text-xs text-gray-400 hover:text-gray-600"
                  onClick={() => openDoc(term.docType, term.label.replace(' 동의', ''))}
                >
                  보기
                </button>
              </div>
            ))}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* 추가 정보 입력 */}
          <div className="space-y-2">
            <Label>추가 정보</Label>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2">
              <p className="text-sm text-blue-700">
                ℹ️ 출발지와 도착지 출입방법, 공동현관 비밀번호, 주차위치 등 배송에 필요한 정보가 있으면 반드시 기재해 주세요.
              </p>
            </div>
            <textarea
              value={additionalNote}
              onChange={(e) => setAdditionalNote(e.target.value)}
              placeholder="예) 아파트 정문에서 세대호출 해주세요, 도착지 공동현관 비밀번호 1234#"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A3A5C] resize-none"
              rows={3}
            />
          </div>

          {/* 안내문 + 동의 체크박스 */}
          <div className={cn(
            'bg-yellow-50 border rounded-lg p-3 space-y-2',
            noticeError ? 'border-red-500 ring-2 ring-red-200' : 'border-yellow-200',
          )}>
            <p className="text-sm text-yellow-800">
              물품 정보가 실제와 상당한 차이가 있거나, 기사 1인이 운반하기 불가능한 무게(45kg이상) 또는 상태(수거가 불가능한 위치 또는 파손되기 쉬운 상태)인 경우,<br/>
              정보의 부족 또는 오류(주소, 연락처, 물품 위치, 공동출입문 비밀번호, 등)로 수거 또는 배송이 불가능한 경우,<br/>
              현장에서 배송이 취소될 수 있으며 약관의 환불 및 취소 정책에 따릅니다.
            </p>
            <label className="flex items-center gap-2 cursor-pointer pt-1">
              <Checkbox
                checked={noticeAgreed}
                onCheckedChange={(checked) => setNoticeAgreed(!!checked)}
              />
              <span className="text-sm font-medium text-yellow-900">위 내용을 확인하였으며 동의합니다.</span>
            </label>
            {noticeError && (
              <p className="text-sm text-red-500">위 내용을 확인하고 동의해주세요</p>
            )}
          </div>

          {/* 예약 신청 버튼 → 주문 생성 후 결제위젯 표시 */}
          <Button
            onClick={() => {
              if (!isAdmin && !canPay) {
                setAttempted(true);
                return;
              }
              handleCreateOrder();
            }}
            disabled={processing}
            className={cn(
              'w-full',
              canPay || isAdmin
                ? 'bg-[#1A3A5C] hover:bg-[#15304D] text-white'
                : 'bg-gray-300 hover:bg-gray-400 text-gray-700',
            )}
            size="lg"
          >
            {processing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                주문 생성 중...
              </>
            ) : (
              <>예약하기 · {formatPrice(finalPriceAfterCoupon)}</>
            )}
          </Button>
        </>
      )}

      {/* Phase 2: 결제위젯 */}
      {phase === 'payment' && orderId && (
        <>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              주문이 생성되었습니다. 아래에서 결제를 완료해주세요.
            </p>
          </div>

          <TossPaymentWidget
            ref={paymentWidgetRef}
            amount={chargeAmount ?? finalPriceAfterCoupon}
            orderId={orderId}
            orderName={categoryLabel ?? '배송 서비스'}
            customerPhone={phone}
          />

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <Button
            onClick={handleRequestPayment}
            disabled={paymentProcessing}
            className="w-full bg-[#1A3A5C] hover:bg-[#15304D] text-white"
            size="lg"
          >
            {paymentProcessing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                결제 처리 중...
              </>
            ) : (
              <>{formatPrice(finalPriceAfterCoupon)} 결제하기</>
            )}
          </Button>
        </>
      )}

      {/* 약관 문서 다이얼로그 */}
      <Dialog open={docDialog.open} onOpenChange={(open) => setDocDialog((prev) => ({ ...prev, open }))}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{docDialog.title}</DialogTitle>
          </DialogHeader>
          {docLoading ? (
            <div className="py-8 text-center text-gray-400">불러오는 중...</div>
          ) : (
            <DocContent content={docDialog.content} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
